/**
 * Debit Note Mapper
 * Maps between API DTOs and Domain Entities
 */

import {
  DebitNoteEntity,
  DebitNoteDataEntity,
  DebitNotesResultEntity,
  DebitNoteCountsEntity,
  PaginationInfo,
} from "../../domain/entities/DebitNoteEntity";

/**
 * Helper function to parse debit note JSON if it's a string
 */
export const parseDebitNoteJson = (
  debitNoteJsonString: string | DebitNoteDataEntity
): DebitNoteDataEntity | null => {
  if (!debitNoteJsonString) {
    return null;
  }

  try {
    if (typeof debitNoteJsonString === "string") {
      return JSON.parse(debitNoteJsonString) as DebitNoteDataEntity;
    }
    return debitNoteJsonString;
  } catch (error) {
    console.error("Error parsing debit note json:", error);
    return null;
  }
};

const toOptionalNumber = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeDebitNoteJson = (
  json: DebitNoteDataEntity | null
): DebitNoteDataEntity | null => {
  if (!json) return null;

  return {
    ...json,
    sub_total: toOptionalNumber(json.sub_total),
    tax_total: toOptionalNumber(json.tax_total),
    total: toOptionalNumber(json.total),
    line_items: Array.isArray(json.line_items)
      ? json.line_items.map((item) => ({
          ...item,
          quantity: toOptionalNumber(item.quantity),
          rate: toOptionalNumber(item.rate),
          item_total: toOptionalNumber(item.item_total),
        }))
      : json.line_items,
  };
};

export class DebitNoteMapper {
  /**
   * Map API response to DebitNoteEntity
   */
  static toDebitNoteEntity(apiResponse: any): DebitNoteEntity {
    const json = normalizeDebitNoteJson(
      parseDebitNoteJson(apiResponse.json || apiResponse.debit_note_json || {})
    );

    return {
      id: apiResponse.id || apiResponse.debit_note_id || 0,
      jobid: apiResponse.jobid || null,
      companyid: apiResponse.company_id || apiResponse.companyid || null,
      filename: apiResponse.filename || null,
      original_filename: apiResponse.original_filename || null,
      file_size: apiResponse.file_size || null,
      folder_id: apiResponse.folder_id || null,
      json: json || ({} as DebitNoteDataEntity),
      reviewed: apiResponse.reviewed?.toString() || "0",
      status: apiResponse.status?.toString() || "0",
      is_under_review: apiResponse.is_under_review ?? 0,
      zoho_id: apiResponse.zoho_id || null,
      posted_to_zoho_date: apiResponse.posted_to_zoho_date || null,
      document_number: apiResponse.document_number || null,
      deleted: apiResponse.deleted || 0,
      created_at: apiResponse.created_at || "",
      updated_at: apiResponse.updated_at || "",
      signed_url: apiResponse.signed_url || null,
      signed_url_expires_at: apiResponse.signed_url_expires_at || null,
      // ZOUR-19 review 12: the server's GST answer for this note, resolved from
      // the PARENT invoice's date. This mapper builds the entity field by
      // field, so a new key has to be named here or it is silently dropped —
      // which is exactly what happened first time round, and the review page
      // fell back to guessing from the invoice picker.
      gst: apiResponse.gst ?? undefined,
    };
  }

  /**
   * Map API response to DebitNotesResultEntity
   */
  static toDebitNotesResultEntity(apiResponse: any): DebitNotesResultEntity {
    const debitNotes: DebitNoteEntity[] = [];
    const paginationData =
      apiResponse.data?.pagination || apiResponse.pagination || {};

    if (
      apiResponse.data?.debit_notes &&
      Array.isArray(apiResponse.data.debit_notes)
    ) {
      debitNotes.push(
        ...apiResponse.data.debit_notes.map((item: any) =>
          this.toDebitNoteEntity(item)
        )
      );
    } else if (Array.isArray(apiResponse.data)) {
      debitNotes.push(
        ...apiResponse.data.map((item: any) => this.toDebitNoteEntity(item))
      );
    }

    const pagination: PaginationInfo = {
      total: paginationData.total || 0,
      limit: paginationData.limit || 0,
      offset: paginationData.offset || 0,
      has_more: paginationData.has_more || false,
      current_page:
        paginationData.current_page ||
        Math.floor((paginationData.offset || 0) / (paginationData.limit || 1)) +
          1,
      total_pages:
        paginationData.total_pages ||
        Math.ceil((paginationData.total || 0) / (paginationData.limit || 1)),
    };

    return {
      debitNotes,
      pagination,
    };
  }

  /**
   * Map API response to DebitNoteCountsEntity
   */
  static toDebitNoteCountsEntity(apiResponse: any): DebitNoteCountsEntity {
    return {
      pendingCount: Number(apiResponse.pending_count) || 0,
      postedCount: Number(apiResponse.posted_count) || 0,
      rejectedCount: Number(apiResponse.rejected_count) || 0,
      underReviewCount: Number(apiResponse.under_review_count) || 0,
    };
  }
}
